import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft, Loader2, Mail, Send } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { type TelegramWidgetUser, useSession } from "@/entities/session"
import logo from "@/shared/assets/kmk-logo.png"
import { BACKOFFICE_CODE_URL, NAVIGATION_BOT, TELEGRAM_WIDGET_HOSTS } from "@/shared/config/auth"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { cn } from "@/shared/lib/utils"
import { TRANSITION } from "@/shared/lib/motion"
import { Label } from "@/shared/ui/label"
import { SegmentedControl } from "@/shared/ui/segmented-control"

const schema = z.object({
	email: z.string().min(1, "Укажите email").email("Похоже на опечатку в email"),
	password: z.string().min(1, "Укажите пароль"),
})

type LoginForm = z.infer<typeof schema>

/**
 * Бот открывается в отдельной вкладке, а эта остаётся на экране ввода кода. Переход по
 * обычной ссылке мог увести текущую вкладку, поэтому открываем явно и страницу не трогаем.
 */
const openBot = (event: { preventDefault: () => void }) => {
	event.preventDefault()
	const tab = window.open(BACKOFFICE_CODE_URL, "_blank")
	if (tab) tab.opener = null
}

/** В коде 8 букв и цифр, дефис и пробелы человек может ввести как угодно. */
const normalizeCode = (value: string) => value.replace(/[^0-9a-z]/gi, "").toUpperCase()
const CODE_LENGTH = 8

/** Маска кода: KMZ7-P4RT. Дефис появляется сам после четвёртого символа, лишнее отбрасывается. */
const formatCode = (value: string) => {
	const raw = normalizeCode(value).slice(0, CODE_LENGTH)
	return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw
}

declare global {
	interface Window {
		onBackofficeTelegramAuth?: (user: TelegramWidgetUser) => void
	}
}

/** Кнопка «Войти через Telegram» от самого Telegram. Работает только на домене бота. */
function TelegramWidget({ onAuth }: { onAuth: (user: TelegramWidgetUser) => void }) {
	const container = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const host = container.current
		if (!host) return
		window.onBackofficeTelegramAuth = onAuth
		const script = document.createElement("script")
		script.src = "https://telegram.org/js/telegram-widget.js?22"
		script.async = true
		script.dataset.telegramLogin = NAVIGATION_BOT
		script.dataset.size = "large"
		script.dataset.radius = "12"
		script.dataset.onauth = "onBackofficeTelegramAuth(user)"
		host.appendChild(script)
		return () => {
			host.replaceChildren()
			window.onBackofficeTelegramAuth = undefined
		}
	}, [onAuth])
	return <div ref={container} className="flex min-h-10 justify-center" />
}

function EmailForm() {
	const { login, isLoggingIn } = useSession()
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginForm>({
		resolver: zodResolver(schema),
		defaultValues: { email: "", password: "" },
	})

	return (
		<form className="space-y-5" onSubmit={handleSubmit((values) => login(values))}>
			<div className="space-y-2">
				<Label htmlFor="email">Email</Label>
				<Input
					id="email"
					placeholder="name@example.com"
					type="email"
					autoComplete="username"
					autoFocus
					aria-invalid={!!errors.email}
					{...register("email")}
				/>
				{errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
			</div>

			<div className="space-y-2">
				<Label htmlFor="password">Пароль</Label>
				<Input
					id="password"
					placeholder="Пароль"
					type="password"
					autoComplete="current-password"
					aria-invalid={!!errors.password}
					{...register("password")}
				/>
				{errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
			</div>

			<Button type="submit" size="lg" className="w-full" disabled={isLoggingIn}>
				{isLoggingIn && <Loader2 className="size-4 animate-spin" />}
				Войти
			</Button>
		</form>
	)
}

/** Левая панель с брендом. На узких экранах скрыта, остаётся только форма. */
function BrandPanel() {
	return (
		<aside className="relative hidden overflow-hidden border-r border-border bg-card lg:flex lg:flex-col lg:justify-between lg:p-12">
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(var(--foreground)_1px,transparent_1px),linear-gradient(90deg,var(--foreground)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_30%_20%,black,transparent_70%)]"
			/>
			<div
				aria-hidden
				className="pointer-events-none absolute -bottom-40 -left-32 size-[34rem] rounded-full bg-primary/25 blur-[120px]"
			/>

			<div className="relative flex items-center gap-3">
				<img src={logo} alt="КМК" className="size-10 rounded-xl shadow-lg shadow-primary/30" />
				<span className="text-lg font-semibold tracking-tight">
					Бэкофис
				</span>
			</div>

			<div className="relative max-w-md">
				<h2 className="text-4xl font-semibold leading-[1.1] tracking-tight text-balance">
					Всё, чем живёт клуб,
					<br />
					<span className="text-primary">под одной крышей</span>
				</h2>
			</div>

			<div aria-hidden />
		</aside>
	)
}

/** Полоска прогресса: пройденные шаги закрашены, подпись справа. */
function Progress({ step }: { step: 1 | 2 }) {
	return (
		<div className="flex items-center gap-3">
			<div className="flex flex-1 gap-1.5">
				{[1, 2].map((n) => (
					<span
						key={n}
						className={cn(
							"h-1 flex-1 rounded-full transition-colors duration-300",
							n <= step ? "bg-primary" : "bg-border",
						)}
					/>
				))}
			</div>
			<span className="text-xs text-muted-foreground">Шаг {step} из 2</span>
		</div>
	)
}

/**
 * Смена содержимого вкладок без рывка: уходящая вкладка гаснет поверх, новая проявляется,
 * а высота блока плавно тянется к высоте новой, поэтому всё, что ниже, не прыгает.
 */
function TabSwap({ id, children }: { id: string; children: ReactNode }) {
	const [height, setHeight] = useState<number | null>(null)
	const current = useRef(id)
	current.current = id

	return (
		<div
			className="relative -m-1 overflow-clip p-1 transition-[height] duration-[320ms] ease-[var(--ease-out-soft)]"
			style={height === null ? undefined : { height: height + 8 }}
		>
			<AnimatePresence initial={false} mode="popLayout">
				<motion.div
					key={id}
					initial={{ opacity: 0, y: 6 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -6, transition: TRANSITION.fast }}
					transition={{ ...TRANSITION.base, delay: 0.08 }}
				>
					<div
						ref={(node) => {
							if (!node) return
							// Высота нужна в том же кадре, когда вкладка появилась: иначе блок начинает тянуться на 3-4 кадра позже.
							if (current.current === id) setHeight(node.offsetHeight)
							const observer = new ResizeObserver(() => {
								if (current.current === id) setHeight(node.offsetHeight)
							})
							observer.observe(node)
							return () => observer.disconnect()
						}}
					>
						{children}
					</div>
				</motion.div>
			</AnimatePresence>
		</div>
	)
}

/** Вход в бэкофис: через Telegram (код из бота или виджет), запасной путь по email и паролю. */
export function LoginPage() {
	const { loginWithCode, loginWithWidget, isLoggingIn, error } = useSession()
	const [code, setCode] = useState("")
	const [withEmail, setWithEmail] = useState(true)
	const [step, setStep] = useState<1 | 2>(1)
	const widget = TELEGRAM_WIDGET_HOSTS.includes(window.location.hostname)
	const ready = normalizeCode(code).length === CODE_LENGTH

	return (
		<div className="grid min-h-dvh bg-background lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
			<BrandPanel />

			<main className="relative grid place-items-center p-6 sm:p-10">
				<div className="w-full max-w-sm space-y-8">
					<div className="space-y-2">
						<div className="flex items-center gap-2.5 lg:hidden">
							<img src={logo} alt="КМК" className="size-9 rounded-lg" />
							<span className="text-sm font-semibold tracking-tight">
								Бэкофис
							</span>
						</div>
						<h1 className="text-3xl font-semibold tracking-tight">С возвращением</h1>
						<p className="text-sm text-muted-foreground">Войдите, чтобы продолжить работу в панели</p>
					</div>

					<SegmentedControl
						aria-label="Способ входа"
						className="flex w-full [&>button]:flex-1 [&>button]:py-2.5"
						options={[
							{ value: "email", label: <span className="inline-flex items-center gap-2"><Mail className="size-4" />Email</span> },
							{ value: "telegram", label: <span className="inline-flex items-center gap-2"><Send className="size-4" />Telegram</span> },
						]}
						value={withEmail ? "email" : "telegram"}
						onChange={(value) => setWithEmail(value === "email")}
					/>

					<div className="space-y-6">
						<TabSwap id={withEmail ? "email" : `telegram-${step}`}>
						{withEmail ? (
							<EmailForm />
						) : (
							<div className="space-y-6">
								{widget ? (
									<>
										<TelegramWidget onAuth={(user) => void loginWithWidget(user)} />
										<div className="flex items-center gap-3 text-xs text-muted-foreground">
											<span className="h-px flex-1 bg-border" />
											или кодом из бота
											<span className="h-px flex-1 bg-border" />
										</div>
									</>
								) : null}

								<Progress step={step} />
								{step === 1 ? (
									<div className="space-y-4">
										<div className="space-y-1">
											<h2 className="text-lg font-semibold">Получите код в Telegram</h2>
											<p className="text-sm text-muted-foreground">
												Откройте бота @{NAVIGATION_BOT}, он пришлёт одноразовый код в личные сообщения
											</p>
										</div>
										<Button asChild size="lg" variant={widget ? "outline" : "default"} className="w-full">
											<a
												href={BACKOFFICE_CODE_URL}
												target="_blank"
												rel="noreferrer"
												onClick={(event) => {
													openBot(event)
													setStep(2)
												}}
											>
												<Send />
												Перейти в бота
											</a>
										</Button>
										<button
											type="button"
											className="block w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
											onClick={() => setStep(2)}
										>
											У меня уже есть код
										</button>
									</div>
								) : (
									<form
										className="space-y-4"
										onSubmit={(event) => {
											event.preventDefault()
											if (ready) void loginWithCode(normalizeCode(code))
										}}
									>
										<div className="space-y-1">
											<Label htmlFor="code" className="block text-lg font-semibold">
												Введите код
											</Label>
											<p className="text-sm text-muted-foreground">8 символов из сообщения бота, дефис не обязателен</p>
										</div>
										<Input
											id="code"
											autoFocus
											placeholder="KMZ7-P4RT"
											autoComplete="one-time-code"
											className="h-14 text-center font-mono text-xl tracking-[0.3em] uppercase"
											value={code}
											autoCapitalize="characters"
											autoCorrect="off"
											spellCheck={false}
											onChange={(event) => {
												const input = event.target
												const caret = input.selectionStart ?? input.value.length
												const typed = normalizeCode(input.value.slice(0, caret)).length
												const next = formatCode(input.value)
												setCode(next)
												// Дефис вставляется в строку, поэтому курсор ставим по числу символов до него.
												requestAnimationFrame(() => {
													const position = Math.min(typed > 4 ? typed + 1 : typed, next.length)
													input.setSelectionRange(position, position)
												})
											}}
										/>
										<Button type="submit" size="lg" className="w-full" disabled={!ready || isLoggingIn}>
											{isLoggingIn && <Loader2 className="size-4 animate-spin" />}
											Войти
										</Button>
										<div className="flex items-center justify-between text-sm text-muted-foreground">
											<button
												type="button"
												className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
												onClick={() => setStep(1)}
											>
												<ArrowLeft className="size-4" />
												Назад
											</button>
											<a
												href={BACKOFFICE_CODE_URL}
												target="_blank"
												rel="noreferrer"
												className="transition-colors hover:text-foreground"
												onClick={openBot}
											>
												Открыть бота снова
											</a>
										</div>
									</form>
								)}
							</div>
						)}
						</TabSwap>

						{error && (
							<p
								role="alert"
								className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
							>
								{error}
							</p>
						)}
					</div>
				</div>
			</main>
		</div>
	)
}
